import { IJsonPatch, onPatch, types } from 'mobx-state-tree';
import { dbSvc } from '../main/DbSvc';
import { IEntity, IEntityUpserter } from '../main/entities';
import { entityFetcherSvc } from '../main/EntityFetcherSvc';
import { mg } from '../mg/mg';
import { MgHasher } from '../mg/MgHasher';
import { IOcResponse } from '../services/Communicator';
import { toasterSvc } from '../services/ToasterSvc';
import { windowSvc } from '../services/WindowSvc';
import { EntityUpserter } from './EntityUpserter';
import { getOmnistore, IOmnistore } from './Omnistore';

type IFetchMode = 'NORMAL' | 'DIRECT' | 'INVALIDATE'

export const EntityStore = types
	.model('EntityStore', {
		id: types.identifier,
		etag: '',
		stateDb: types.optional(types.enumeration(['INIT', 'PENDING', 'DONE']), 'INIT'),
		stateNet: types.optional(types.enumeration(['INIT', 'PENDING', 'DONE']), 'INIT'),
		timestampNet: 0,
		_map: types.map(types.frozen<IEntity>()),
	})
	.views(self => ({
		get _omnistore() : IOmnistore {
			return getOmnistore();
		}
	}))
	.views(self => ({
		reoctoStore() {
			return self._omnistore.reoctoStore;
		},
	}))
	.views(self => ({
		get _extendedInfo() {
			const i = self.id.indexOf(':');
			const id = self.id;
			const entityClassName = i > -1 ? self.id.substring(0, i) : self.id;
			const masterId = i > -1 ? +self.id.substring(i + 1) : 0;
			const isSyncing = entityClassName === 'TemplateItem';
			const etag = self.etag;

			const pathFn = () => {
				if (isSyncing) return 'UpdateTemplateItems';
				switch (entityClassName) {
				case 'Supplier': return 'SearchOwnSupplier';
				case 'AdvertisementItem': return 'SearchAdvertiserOfferInCatalogs';
				case 'Reminder': return 'SearchTemplateReminder';
				}
				return 'Search' + entityClassName;
			};

			const path = pathFn();
			return { id, entityClassName, masterId, isSyncing, path, etag };
		},
	}))
	.volatile(self => ({
		_upserter: EntityUpserter.create({
			_storeId: self.id,
			_isSyncing: self._extendedInfo.isSyncing,
			_entityClassName: self._extendedInfo.entityClassName,
			_masterId: self._extendedInfo.masterId,
		}),
	}))
	.actions(self => ({
		clear() {
			self.etag = '';
			self.stateDb = 'INIT';
			self.stateNet = 'INIT';
			self.timestampNet = 0;
			self._map.clear();
			self._upserter.clear();
		},
	}))
	.views(self => ({
		get length() {
			return self._map.size;
		},
		get allAsList(): Array<IEntity> {
			return self._upserter.allAsList(self._map);
		},
		byId(id: string | number | undefined) {
			return id ? self._map.get('' + id) : undefined;
		},
		get upserter(): IEntityUpserter<any> {
			return self._upserter;
		},
	}))
	.actions(self => ({
		_mergeEntity(e: IEntity) {
			const old = self.byId(e.id);
			if (!old || !old.timestamp || e.timestamp && old.timestamp <= e.timestamp) self._map.set('' + e.id, mg.freezeIfDevelopment(e));
		},
	}))
	.actions(self => ({
		_setStateDb(stateDb: typeof self.stateDb) {
			self.stateDb = stateDb;
		},
		_finishFetchDb({ etag, list }: { etag: string, list: Array<IEntity> }) {
			//console.log('_finishFetchDb', self.id, etag, list.length)
			if (!etag) return;
			if (etag === self.etag) return;
			list.forEach(e => self._mergeEntity(e));
			self.etag = etag;
		},
	}))
	.actions(self => ({
		_setStateNet(stateNet: typeof self.stateNet) {
			self.stateNet = stateNet;
		},
		_finishFetchNet({ etag, list, isComplete, deletions }, patches) {
			if (etag && etag === self.etag) return 'ok';
			//console.log('_finishFetchNet', self.id, self.etag + "->" + etag, list && list.length, isComplete, deletions)
			const missing = new Set<string>();
			if (isComplete) self._map.forEach((v, k) => missing.add(k + ''));
			list.forEach(e => {
				self._mergeEntity(e);
				missing.delete(e.id + '');
			});
			missing.forEach(id => self._map.delete(id));
			deletions.forEach(id => self._map.delete(id));
			self.etag = etag;
			self.timestampNet = windowSvc.currentTimeMillis();
			dbSvc.patch(self._extendedInfo, etag, patches);
			return 'ok';
		},
	}))
	.actions(self => ({
		_startFetchDb() {
			if (!self.reoctoStore().useDb) return;
			if (self.stateDb === 'PENDING') return;
			self.stateDb = 'PENDING';
			const { id, entityClassName, masterId } = self._extendedInfo;
			return dbSvc.fetch({ id, entityClassName, masterId })
				.then(self._finishFetchDb)
				.catch(r => console.error('_startFetchDb', self.id, r))
				.finally(() => self._setStateDb('DONE'));
		},
	}))
	.actions(self => {
		const promises: Array<{ resolve: any, reject: any }> = []; //TODO

		async function handleResponse(responsePromise: Promise<IOcResponse>, successMessage: string) : Promise<unknown> {
			const patches: Array<IJsonPatch> = [];
			try {
				const response = await responsePromise;
				toasterSvc.handleResponse(successMessage)(response);
				if (!response.outputList) {
					while (promises.length) promises.shift()!.reject();
				} else {
					let etag = response.etag;
					if (!etag && response.request.method!=='GET') {
						etag = new MgHasher().addString(self.etag).addUnordered(response.outputList.map(e => e.id + ' ' + e.timestamp)).toString();
					}
					const agg = {etag, list: response.outputList, isComplete: false, deletions: []};
					while (promises.length) promises.shift()!.resolve(agg);
					self._finishFetchNet(agg, patches);
				}
			} finally {
				onPatch(self._map, p => patches.push(p));
			}
			return undefined;
		}

		function _startFetchNet(mode: IFetchMode) {
			const ignorePending = mode !== 'NORMAL';
			if (mode === 'INVALIDATE') self.etag = '';
			if (!ignorePending && self.stateNet === 'PENDING') {
				const result = new Promise(function (resolve, reject) {
					//console.log('_startFetchNet pushing promise', self.id)
					promises.push({ resolve, reject });
				});
				return result;
			}
			if (!ignorePending) self.stateNet = 'PENDING';
			const patches: Array<IJsonPatch> = [];
			const disposer = onPatch(self._map, p => patches.push(p));
			return entityFetcherSvc.fetch(self._extendedInfo as any, self.etag) //TODO
				.then(r => {
					//console.log('_startFetchNet r:', r);
					//console.log('_startFetchNet resolving promises ' + promises.length, self.id);
					// if (!r.list) return Promise.reject('No list in fetch result.'); //TODO
					while (promises.length) promises.shift()!.resolve(r);
					return self._finishFetchNet(r, patches);
				})
				.catch(r => {
					console.warn('_startFetchNet', r);
					while (promises.length) promises.shift()!.reject(r);
					//console.error("_startFetchNet", self.id, r)
				})
				.finally(() => disposer())
				.finally(() => ignorePending || self._setStateNet('DONE'));
		}

		return {
			handleResponse,
			_startFetchNet,
		};
	})
	.actions(self => ({
		fetch(mode?: IFetchMode) {
			if (self.stateDb === 'INIT') self._startFetchDb();
			return self._startFetchNet(mode || 'NORMAL');
		},
	}))
	.actions(self => ({
		/** Like fetch, but ignore pending request. */ //TODO remove
		fetchDirect() {
			return self.fetch('DIRECT');
		},
	}));
